import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { sortWith } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { getMessage } from '@gef-ui/localization/messages';
import Pencil from '@gef-ui/material-icons/Pencil';

import MODALS from 'constants/modals';
import { sortAlphabeticallyBy } from 'utils/sort';
import { joinString } from 'utils/string';

import Manager from './Manager';

export const Agency = ({ agency }) => {
	const handleOpenAgencyDetailModal = useCallback(() => {
		NiceModal.show(MODALS.agency_detail, { agency });
	}, [agency]);

	const sortedAssignedManagers = useMemo(
		() => sortWith([sortAlphabeticallyBy('surname'), sortAlphabeticallyBy('firstname')])(agency?.assignedManagers),

		[agency?.assignedManagers],
	);

	return (
		<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
						<Typography variant="h5" sx={{ color: 'text.primary' }}>
							{joinString(' - ', [agency?.areaId, agency?.name])}
						</Typography>
						<IconButton size="small" onClick={handleOpenAgencyDetailModal}>
							<Pencil sx={{ color: 'primary.main' }} />
						</IconButton>
					</Stack>
				</Grid>
				<Grid size={6}>
					<Stack>
						<Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.75 }}>
							{getMessage('settings:marketPlaceAgencies.assignedZipCodes')}
						</Typography>
						{!isNilOrEmpty(agency?.zipCodeRanges) &&
							agency?.zipCodeRanges.map((zipCode) => (
								<Typography variant="body1">
									{joinString(' - ', [zipCode?.from, zipCode?.to])}
								</Typography>
							))}
					</Stack>
				</Grid>
				<Grid size={6}>
					<Stack>
						<Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.75 }}>
							{getMessage('settings:marketPlaceAgencies.managersInAgency')}
						</Typography>
						{isNilOrEmpty(sortedAssignedManagers) ? (
							<Typography variant="body1">
								{getMessage('settings:marketPlaceAgencies.noManagersAvailable')}
							</Typography>
						) : (
							sortedAssignedManagers.map((manager) => (
								<Manager manager={manager} showIcon withTooltip assigned />
							))
						)}
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	);
};

Agency.propTypes = {
	agency: PropTypes.shape({
		areaId: PropTypes.string,
		assignedManagers: PropTypes.array,
		name: PropTypes.string,
		zipCodeRanges: PropTypes.array,
	}),
};

export default Agency;
